import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CaretDown, CaretRight, ArrowsClockwise, DownloadSimple } from "@phosphor-icons/react";
import api, { formatApiError } from "../../lib/api";

// Compact per-action styling — kept minimal so 30 rows read at a glance.
const ACTION_CFG = {
  invite:  { label: "INVITED",   cls: "bg-zinc-50 text-zinc-700 border-zinc-300" },
  nudge:   { label: "NUDGED",    cls: "bg-amber-50 text-amber-700 border-amber-300" },
  accept:  { label: "ACCEPTED",  cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  rescind: { label: "RESCINDED", cls: "bg-red-50 text-[#D0021B] border-red-300" },
  expire:  { label: "EXPIRED",   cls: "bg-zinc-100 text-zinc-500 border-zinc-300" },
};

// Format an ISO timestamp as "Aug 02, 15:25 UTC".
const fmtTs = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return iso; }
};

/**
 * Invitation Audit Log panel — collapsible history table under Pending
 * Invitations. Sourced from `/tenant/invitations/history` which reads
 * `audit_logs` for the caller's tenant. Kept compact (max 500 rows,
 * one row per event) so admins can scan a month of activity at a glance.
 */
export default function InvitationHistoryPanel() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tenant/invitations/history?days=30");
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load invitation history");
    } finally {
      setLoading(false);
    }
  };

  // Lazy-load: only hit the API when the admin first opens the panel.
  useEffect(() => { if (open && items.length === 0) load(); }, [open]);

  const downloadCsv = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/tenant/invitations/history.csv?days=30", {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv;charset=utf-8;" }));
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `invitation-history-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Invitation history exported");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to export CSV");
    } finally {
      setExporting(false);
    }
  };

  const rows = filter === "all" ? items : items.filter((r) => r.action === filter);

  return (
    <div className="border border-zinc-200 mt-6" data-testid="invitation-history-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="invitation-history-toggle"
        className="w-full flex items-center justify-between px-4 py-2.5 bg-zinc-50 hover:bg-zinc-100 text-left"
      >
        <div className="flex items-center gap-2">
          {open ? <CaretDown size={12} className="text-zinc-500" /> : <CaretRight size={12} className="text-zinc-500" />}
          <span className="font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-700">
            Invitation history · last 30d
          </span>
        </div>
        <div className="flex items-center gap-2 text-[10px] font-mono text-zinc-500">
          {Object.entries(ACTION_CFG).map(([k, cfg]) => (
            <span key={k} className={`px-1.5 py-0.5 border ${cfg.cls}`}>
              {cfg.label} · {counts[k] || 0}
            </span>
          ))}
        </div>
      </button>

      {open && (
        <div className="border-t border-zinc-200">
          <div className="px-4 py-2 border-b border-zinc-100 flex items-center gap-2 bg-white">
            <div className="flex border border-zinc-950" data-testid="invitation-history-filter">
              {[["all", "All"], ...Object.entries(ACTION_CFG).map(([k, cfg]) => [k, cfg.label])].map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setFilter(k)}
                  data-testid={`history-filter-${k}`}
                  className={`px-2.5 py-1 text-[10px] font-mono tracking-[0.15em] uppercase border-l first:border-l-0 border-zinc-950 ${
                    filter === k ? "bg-zinc-950 text-white" : "bg-white text-zinc-700 hover:bg-zinc-100"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={downloadCsv}
              disabled={exporting || items.length === 0}
              data-testid="invitation-history-csv"
              title="Download the last 30 days of invitation events as CSV"
              className="text-[10px] font-mono text-zinc-500 hover:text-zinc-950 flex items-center gap-1 ml-auto disabled:opacity-40"
            >
              <DownloadSimple size={11} /> {exporting ? "EXPORTING…" : "DOWNLOAD CSV"}
            </button>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              data-testid="invitation-history-refresh"
              className="text-[10px] font-mono text-zinc-500 hover:text-zinc-950 flex items-center gap-1 disabled:opacity-40"
            >
              <ArrowsClockwise size={11} /> {loading ? "LOADING…" : "REFRESH"}
            </button>
          </div>

          {loading && items.length === 0 ? (
            <div className="p-6 text-center text-xs font-mono text-zinc-400">LOADING…</div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-center text-xs font-mono text-zinc-400" data-testid="invitation-history-empty">
              NO EVENTS IN THIS WINDOW
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-[10px] font-mono tracking-[0.15em] uppercase text-zinc-500">
                  <th className="text-left px-4 py-2">When</th>
                  <th className="text-left px-4 py-2">Action</th>
                  <th className="text-left px-4 py-2">Email</th>
                  <th className="text-left px-4 py-2">Role</th>
                  <th className="text-left px-4 py-2">Actor</th>
                </tr>
              </thead>
              <tbody data-testid="invitation-history-tbody">
                {rows.map((r) => {
                  const cfg = ACTION_CFG[r.action] || { label: r.action.toUpperCase(), cls: "bg-zinc-50 text-zinc-700 border-zinc-300" };
                  // Compose a stable key from the immutable event tuple —
                  // timestamps are ISO strings, actions are enum-ish, emails
                  // are canonicalised — so React can reuse rows correctly
                  // when the admin filters/refreshes.
                  const rowKey = `${r.timestamp}|${r.action}|${r.email}`;
                  return (
                    <tr key={rowKey} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="px-4 py-2 tnum text-xs font-mono text-zinc-500 whitespace-nowrap">{fmtTs(r.timestamp)}</td>
                      <td className="px-4 py-2">
                        <span className={`text-[10px] font-mono px-1.5 py-0.5 border ${cfg.cls}`}>
                          {cfg.label}
                        </span>
                        {r.action === "nudge" && r.detail?.hours_left != null && (
                          <span className="ml-1 text-[10px] font-mono text-zinc-400">{r.detail.hours_left}h left</span>
                        )}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">{r.email}</td>
                      <td className="px-4 py-2 text-xs text-zinc-500 uppercase">{r.role || "—"}</td>
                      <td className="px-4 py-2 text-xs text-zinc-500 font-mono">{r.actor}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
